import { type GetProps, XStack, YStack } from "tamagui";

type ProgressBarProps = Omit<GetProps<typeof XStack>, "children"> & {
  value?: number;
  max?: number;
  indicatorColor?: GetProps<typeof YStack>["backgroundColor"];
  trackColor?: GetProps<typeof YStack>["backgroundColor"];
  "aria-label"?: string;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export default function ProgressBar({
  value = 0,
  max = 100,
  indicatorColor = "$fillPrimary",
  trackColor = "$contentLoaderBackground",
  "aria-label": ariaLabel = "Progress",
  ...props
}: ProgressBarProps) {
  const numericValue = typeof value === "number" ? value : 0;
  const numericMax = typeof max === "number" && max > 0 ? max : 100;
  const progressValue = clamp(numericValue, 0, numericMax);
  const progressPercentage = (progressValue / numericMax) * 100;

  return (
    <XStack
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={numericMax}
      aria-valuenow={progressValue}
      aria-valuetext={`${Math.round(progressPercentage)}%`}
      height="$50"
      width="100%"
      borderRadius={100000}
      overflow="hidden"
      backgroundColor={trackColor}
      {...props}
    >
      <YStack
        height="100%"
        width={`${progressPercentage}%`}
        backgroundColor={indicatorColor}
        borderRadius={100000}
      />
    </XStack>
  );
}
